class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;

    this.drawerClass = "wt-cart__drawer";

    this.drawer = this.querySelector(`.${this.drawerClass}`);

    this.classDrawerActive = `${this.drawerClass}--open`;

    this.body = document.body;

    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.page-overlay-cart`,
    ].join(", ");

    this.triggers = () =>
      document.querySelectorAll(this.triggerQuery);

    this.isOpen = false;

    this.isCartPage =
      window.location.pathname ===
      window.routes.cart_url;

    this.closeButton = () =>
      this.querySelector(".wt-cart__drawer__close");

    this.mainTrigger =
      document.querySelector(".wt-cart__trigger");

    this.toggleEelements = () =>
      this.querySelectorAll(
        this.dataset.toggleTabindex,
      );

    // Store opener element
    this.openerEl = null;
  }

  connectedCallback() {
    if (
      this.cartType === "page" ||
      this.isCartPage
    ) {
      document.addEventListener(
        "cart-drawer:refresh",
        (e) => this.refreshCartDrawer(e),
      );

      return;
    }

    this.init();

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        if (this.isOpen) {
          setTabindex(
            this.toggleEelements(),
            "0",
          );

          if (this.closeButton()) {
            this.closeButton().focus();
          }
        }
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // ===================================
  // REMEMBER OPENER
  // ===================================

  rememberOpener(e) {
    const current = e?.currentTarget;

    const active = document.activeElement;

    const interactive =
      current instanceof HTMLElement
        ? current.closest?.(
            "button, a, [tabindex], input, select",
          ) || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  // ===================================
  // RESTORE FOCUS
  // ===================================

  restoreFocusToOpener() {
    const el = this.openerEl;

    if (!el) return;

    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") ===
        "true";

    if (isDisabled) return;

    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  // ===================================
  // GET FOCUSABLE ELEMENTS
  // ===================================

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";

    const focusableElements = () =>
      Array.from(
        this.querySelectorAll(
          focusableElementsSelector,
        ),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last:
        focusableElements()[
          focusableElements().length - 1
        ],
    };
  }

  // ===================================
  // OPEN DRAWER
  // ===================================

  openDrawer() {
    this.isOpen = true;

    this.setAttribute("open", "");

    this.drawer.classList.add(
      this.classDrawerActive,
    );

    // REMOVE BROKEN THEME CLASSES
    document.body.classList.remove(
      "drawers-animated",
      "page-overlay-cart-on",
      "no-focus-visible",
    );

    setTabindex(
      this.toggleEelements(),
      "0",
    );

    if (this.closeButton()) {
      this.closeButton().focus();
    }

    document.dispatchEvent(
      new CustomEvent(
        PUB_SUB_EVENTS.cartDrawerOpen,
        {
          bubbles: true,
        },
      ),
    );
  }

  // ===================================
  // CLOSE DRAWER
  // ===================================

  closeDrawer() {
    this.isOpen = false;

    this.removeAttribute("open");

    this.drawer.classList.remove(
      this.classDrawerActive,
    );

    // REMOVE BROKEN THEME CLASSES
    document.body.classList.remove(
      "drawers-animated",
      "page-overlay-cart-on",
      "no-focus-visible",
    );

    setTabindex(
      this.toggleEelements(),
      "-1",
    );

    this.restoreFocusToOpener();

    document.dispatchEvent(
      new CustomEvent(
        PUB_SUB_EVENTS.cartDrawerClose,
        {
          bubbles: true,
        },
      ),
    );
  }

  // ===================================
  // TOGGLE
  // ===================================

  toggleDrawerClasses(forceState = null) {
    if (forceState === "open") {
      this.openDrawer();
      return;
    }

    if (forceState === "close") {
      this.closeDrawer();
      return;
    }

    if (this.isOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  // ===================================
  // INIT
  // ===================================

  init() {
    // ESC + TAB LOOP

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" ||
        e.keyCode === 9 ||
        e.code === "Tab";

      const { first, last } =
        this.getFocusableElements();

      // ESC CLOSE

      if (
        e.key === "Escape" ||
        e.keyCode === 27 ||
        e.code === "Escape"
      ) {
        if (this.isOpen) {
          this.closeDrawer();
        }
      }

      // TAB LOOP

      if (isTabPressed) {
        if (
          e.shiftKey &&
          document.activeElement === first
        ) {
          last.focus();

          e.preventDefault();
        } else if (
          !e.shiftKey &&
          document.activeElement === last
        ) {
          first.focus();

          e.preventDefault();
        }
      }
    });

    // ===================================
    // TRIGGERS
    // ===================================

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        if (!this.isOpen) {
          this.rememberOpener(e);
        }

        this.toggleDrawerClasses();
      });
    });

    // ===================================
    // CLOSE BUTTON
    // ===================================

    this.addEventListener("click", (e) => {
      if (
        e.target.classList.contains(
          "wt-cart__drawer__close",
        )
      ) {
        e.preventDefault();

        this.closeDrawer();
      }
    });

    // ===================================
    // REFRESH
    // ===================================

    document.addEventListener(
      "cart-drawer:refresh",
      (e) => this.refreshCartDrawer(e),
    );
  }

  // ===================================
  // RENDER CONTENTS
  // ===================================

  renderContents(
    parsedState,
    shouldOpenDrawer = true,
  ) {
    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement = section.selector
          ? document.querySelector(
              section.selector,
            )
          : document.getElementById(
              section.id,
            );

        if (!sectionElement) return;

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector,
          );
      },
    );

    // REMOVE BROKEN BODY CLASSES
    document.body.classList.remove(
      "drawers-animated",
      "page-overlay-cart-on",
      "no-focus-visible",
    );

    // OPEN DRAWER AFTER AJAX
    if (shouldOpenDrawer) {
      setTimeout(() => {
        this.openDrawer();
      }, 50);
    }
  }

  // ===================================
  // SECTION HTML
  // ===================================

  getSectionInnerHTML(
    html,
    selector = ".shopify-section",
  ) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  // ===================================
  // SECTIONS
  // ===================================

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  // ===================================
  // REFRESH CART
  // ===================================

  refreshCartDrawer() {
    const sectionsToRender =
      this.getSectionsToRender();

    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };

        this.renderContents(
          parsedState,
          false,
        );
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  "cart-drawer",
  CartDrawerSection,
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define(
  "cart-drawer-items",
  CartDrawerItems,
);